import React, { useCallback, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Label } from "@/shared/ui/Label";
import MultipleFilesUploader from "@/shared/ui/MultipleFilesUploader";
import { AgentDocument } from "@/entities/agent/model/agent.types";
import DeleteCircle from "@/shared/ui/icon/DeleteCircle";

interface DocumentUploadSectionProps {
  index: number;
  documents?: AgentDocument[];
  onDocumentsChange: (documents: AgentDocument[]) => void;
  disabled: boolean;
  maxFileSize?: number;
  showToast?: (message: string) => void;
}

export default function DocumentUploadSection({
  index,
  documents = [],
  onDocumentsChange,
  disabled,
  maxFileSize = 15 * 1024 * 1024, // 15MB
  showToast,
}: DocumentUploadSectionProps) {
  const t = useTranslations("Demo");
  const locale = useLocale();

  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileAdd = useCallback(
    async (files: File[], disabled) => {
      if (disabled) {
        return;
      }
      if (documents.length + files.length > 6) {
        showToast?.(t("한 아이템에는 최대 6개의 문서를 업로드 할 수 있습니다"));
        return;
      }

      const newFileSize = files.reduce((acc, cur) => {
        return acc + cur.size;
      }, 0);
      const existingFileSize = documents.reduce((acc, cur) => {
        return acc + cur.size;
      }, 0);
      if ((existingFileSize + newFileSize) / 1000000 > 100) {
        showToast?.(t("전체 파일 용량은 100MB를 넘을 수 없습니다"));
        return;
      }

      const validFiles = files.filter((file) => file.size <= maxFileSize);
      const oversizedFiles = files.filter((file) => file.size > maxFileSize);
      if (oversizedFiles.length > 0) {
        const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
        showToast?.(
          locale === "ko"
            ? `파일 크기는 ${maxSizeMB}MB를 초과할 수 없습니다.`
            : `File size cannot exceed ${maxSizeMB}MB.`
        );
      }

      const newDocuments = validFiles.map((file) => {
        return {
          name: file.name,
          url: "",
          uploaded: false,
          size: file.size,
          file: file,
        };
      });
      const currentDocuments = [...documents, ...newDocuments];
      onDocumentsChange(currentDocuments);
    },
    [documents, onDocumentsChange, showToast, t, disabled]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files).filter(
        (file) =>
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".hwp") ||
          file.name.toLowerCase().endsWith(".hwpx") ||
          file.name.toLowerCase().endsWith(".doc") ||
          file.name.toLowerCase().endsWith(".docx")
      );

      if (files.length > 0) {
        handleFileAdd(files, disabled);
      }
    },
    [handleFileAdd, disabled]
  );

  const handleDocumentRemove = useCallback(
    (docIndex: number) => {
      const updatedDocuments = documents.filter((_, i) => i !== docIndex);
      onDocumentsChange(updatedDocuments);
    },
    [documents, onDocumentsChange]
  );

  return (
    <>
      <div
        className={"flex grow flex-col gap-[12px]"}
        id={`documents-section-${index}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Label
          htmlFor={`file-uploader-${index}`}
          className={"front-[400] text-label1"}
        >
          {t("업로드 파일(최대 6개 가능)")}
        </Label>
        {!documents.length ? (
          <label
            htmlFor={`file-uploader-${index}`}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed py-[12px] transition-colors ${
              isDragOver
                ? "bg-primary-50 border-primary-dark"
                : "border-gray-300 bg-blue-30"
            } `}
          >
            <span
              className={`text-caption1 font-[400] ${
                isDragOver ? "text-primary-dark" : "text-gray-300"
              }`}
            >
              {t("업로드하고 싶은 파일을 첨부해주세요")}
            </span>
            <span
              className={`whitespace-pre-line text-caption1 font-[400] ${
                isDragOver ? "text-primary-dark" : "text-gray-300"
              }`}
            >
              {t("(PDF, HWP, HWPX, DOC, DOCX 업로드 가능)")}
            </span>
          </label>
        ) : (
          <div className={"flex grow flex-col gap-[12px]"}>
            {documents.map((itemData, docIndex) => {
              return (
                <div
                  key={docIndex}
                  className={"flex w-full items-center gap-[4px]"}
                >
                  <span className={"inline-block w-[calc(100%-40px)] truncate"}>
                    {itemData.name}
                  </span>
                  <button
                    className={"text-gray-500"}
                    onClick={() => handleDocumentRemove(docIndex)}
                  >
                    <DeleteCircle />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <MultipleFilesUploader
        disabled={disabled}
        label={t("문서 업로드 하기")}
        labelClassName={"text-body1 text-white"}
        itemIndex={index}
        accept={".pdf, .hwp, .hwpx, .doc, .docx"}
        documents={documents}
        onFileAdd={handleFileAdd}
        onFileRemove={() => {}}
      />
    </>
  );
}
